import { useMemo } from 'react';
import {
  FormField,
  Input,
  Textarea,
  Select,
  Toggle,
  SpaceBetween,
  Container,
  Header,
  ColumnLayout,
  Popover,
  Box,
  Icon,
} from '@cloudscape-design/components';
import { FieldSchema, getTemplate, getConfigTypes } from '../templates/registry';
import { FIELD_TOOLTIPS, OPTION_DESCRIPTIONS, POLLY_VOICE_OPTIONS, FORMAT_HINTS } from '../templates/field-help';
import { ReferenceSelect } from './ReferenceSelect';
import { WeeklyScheduleGrid } from './WeeklyScheduleGrid';
import { BusinessUnitSelect } from './BusinessUnitSelect';

interface GuidedFormProps {
  configType: string;
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  configName: string;
  onConfigNameChange: (name: string) => void;
  configId: string;
  onConfigIdChange: (id: string) => void;
  showConfigTypeSelector?: boolean;
  onConfigTypeChange?: (type: string) => void;
  errors?: Record<string, string>;
}

/**
 * Generates a kebab-case configId suggestion from a configName.
 */
function toKebabCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Guided form that renders structured input controls from a configType's template schema.
 * Requirements: 3.3, 3.4, 3.5, 3.6, 3.8, 3.10, 6.4
 */
export function GuidedForm({
  configType,
  values,
  onChange,
  configName,
  onConfigNameChange,
  configId,
  onConfigIdChange,
  showConfigTypeSelector,
  onConfigTypeChange,
  errors = {},
}: GuidedFormProps) {
  const template = getTemplate(configType);
  const schema = template?.payloadSchema ?? [];
  const configTypes = useMemo(() => getConfigTypes(), []);

  // Auto-suggest configId from configName
  const handleConfigNameChange = (name: string) => {
    onConfigNameChange(name);
    // Only auto-suggest if configId hasn't been manually edited
    const suggestedId = toKebabCase(name);
    const currentSuggested = toKebabCase(configName);
    if (configId === currentSuggested || configId === '') {
      onConfigIdChange(suggestedId);
    }
  };

  const updateField = (key: string, value: unknown) => {
    onChange({ ...values, [key]: value });
  };

  /**
   * Renders a field label with optional ? tooltip and * required marker.
   * BR-13: Every field has a tooltip; required fields marked with *.
   */
  const renderLabel = (field: FieldSchema) => {
    const tooltip = FIELD_TOOLTIPS[field.key] ?? field.helpText ?? field.description;
    return (
      <span>
        {field.label}
        {field.required && <span style={{ color: '#D13212' }}> *</span>}
        {tooltip && (
          <Popover
            dismissButton={false}
            position="top"
            size="small"
            triggerType="custom"
            content={<Box variant="p" padding="s">{tooltip}</Box>}
          >
            <span style={{ marginLeft: '6px', cursor: 'help', color: '#687078' }}>
              <Icon name="status-info" size="small" />
            </span>
          </Popover>
        )}
      </span>
    );
  };

  /**
   * Gets the format hint for a field type.
   */
  const getFormatHint = (field: FieldSchema): string | undefined => {
    if (field.type === 'time') return FORMAT_HINTS.time;
    if (field.key === 'conditionalExpression') return FORMAT_HINTS.conditionalExpression;
    if (field.key === 'externalTransferNumber') return FORMAT_HINTS.e164;
    return undefined;
  };

  /**
   * Gets enriched options with descriptions for select fields.
   */
  const getEnrichedOptions = (field: FieldSchema) => {
    // Special case: Polly Voice with language/gender/flag
    if (field.key === 'pollyVoice') {
      return POLLY_VOICE_OPTIONS;
    }
    // Add descriptions from OPTION_DESCRIPTIONS if available
    const descriptions = OPTION_DESCRIPTIONS[field.key];
    if (descriptions && field.options) {
      return field.options.map((opt) => ({
        ...opt,
        description: descriptions[opt.value] ?? undefined,
      }));
    }
    return field.options ?? [];
  };

  const renderField = (field: FieldSchema) => {
    const fieldValue = values[field.key];
    const errorText = errors[field.key];
    const formatHint = getFormatHint(field);

    switch (field.type) {
      case 'text':
        return (
          <FormField
            key={field.key}
            label={renderLabel(field)}
            description={formatHint}
            errorText={errorText}
          >
            <Input
              value={(fieldValue as string) ?? (field.defaultValue as string) ?? ''}
              onChange={({ detail }) => updateField(field.key, detail.value)}
            />
          </FormField>
        );

      case 'textarea':
        return (
          <FormField
            key={field.key}
            label={renderLabel(field)}
            description={formatHint}
            errorText={errorText}
          >
            <Textarea
              value={(fieldValue as string) ?? (field.defaultValue as string) ?? ''}
              onChange={({ detail }) => updateField(field.key, detail.value)}
              rows={3}
            />
          </FormField>
        );

      case 'number':
        return (
          <FormField
            key={field.key}
            label={renderLabel(field)}
            description={formatHint}
            errorText={errorText}
          >
            <Input
              type="number"
              value={String(fieldValue ?? field.defaultValue ?? '')}
              onChange={({ detail }) => updateField(field.key, Number(detail.value))}
            />
          </FormField>
        );

      case 'boolean':
        return (
          <FormField key={field.key} label={renderLabel(field)}>
            <Toggle
              checked={(fieldValue as boolean) ?? (field.defaultValue as boolean) ?? false}
              onChange={({ detail }) => updateField(field.key, detail.checked)}
            />
          </FormField>
        );

      case 'select':
        return (
          <FormField
            key={field.key}
            label={renderLabel(field)}
            errorText={errorText}
          >
            <Select
              selectedOption={
                getEnrichedOptions(field).find((o) => o.value === fieldValue) ?? null
              }
              onChange={({ detail }) =>
                updateField(field.key, detail.selectedOption?.value ?? '')
              }
              options={getEnrichedOptions(field)}
              placeholder="Select..."
            />
          </FormField>
        );

      case 'time':
        return (
          <FormField
            key={field.key}
            label={renderLabel(field)}
            description={FORMAT_HINTS.time}
            errorText={errorText}
          >
            <Input
              type="text"
              value={(fieldValue as string) ?? (field.defaultValue as string) ?? ''}
              onChange={({ detail }) => updateField(field.key, detail.value)}
              placeholder="HH:MM"
            />
          </FormField>
        );

      case 'date':
        return (
          <FormField
            key={field.key}
            label={renderLabel(field)}
            errorText={errorText}
          >
            <Input
              type="text"
              value={(fieldValue as string) ?? ''}
              onChange={({ detail }) => updateField(field.key, detail.value)}
              placeholder="YYYY-MM-DD"
            />
          </FormField>
        );

      case 'key-value':
        return (
          <FormField
            key={field.key}
            label={renderLabel(field)}
            errorText={errorText}
          >
            <Textarea
              value={
                typeof fieldValue === 'object'
                  ? JSON.stringify(fieldValue, null, 2)
                  : (fieldValue as string) ?? ''
              }
              onChange={({ detail }) => {
                try {
                  updateField(field.key, JSON.parse(detail.value));
                } catch {
                  updateField(field.key, detail.value);
                }
              }}
              rows={4}
              placeholder='{"key": "value"}'
            />
          </FormField>
        );

      case 'reference':
        return (
          <ReferenceSelect
            key={field.key}
            field={field}
            value={(fieldValue as string) ?? ''}
            onChange={(val) => updateField(field.key, val)}
            siblingValues={values}
            error={errorText}
          />
        );

      default:
        return null;
    }
  };

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h3">Configuration Details</Header>}>
        <SpaceBetween size="m">
          <ColumnLayout columns={2}>
            <FormField label="Configuration Name" errorText={errors['configName']}>
              <Input
                value={configName}
                onChange={({ detail }) => handleConfigNameChange(detail.value)}
                placeholder="e.g. Sales Queue Hours"
              />
            </FormField>
            <FormField
              label="Configuration ID"
              description="Lowercase letters, numbers and hyphens only, e.g. claims-queue-hours"
              errorText={errors['configId']}
            >
              <Input
                value={configId}
                onChange={({ detail }) => onConfigIdChange(detail.value)}
                placeholder="e.g. sales-queue-hours"
              />
            </FormField>
          </ColumnLayout>

          {showConfigTypeSelector && (
            <FormField label="Configuration Type" errorText={errors['configType']}>
              <Select
                selectedOption={configTypes.find((t) => t.value === configType) ?? null}
                onChange={({ detail }) => onConfigTypeChange?.(detail.selectedOption?.value ?? '')}
                options={configTypes}
                placeholder="Select a type..."
              />
            </FormField>
          )}

          <BusinessUnitSelect
            value={(values._businessUnit as string) ?? ''}
            onChange={(val) => onChange({ ...values, _businessUnit: val })}
          />
        </SpaceBetween>
      </Container>

      {configType === 'business-hours' && (
        <WeeklyScheduleGrid
          values={values}
          onChange={onChange}
          errors={errors}
        />
      )}

      {schema.length > 0 && configType !== 'business-hours' && (
        <Container header={<Header variant="h3">Payload Fields</Header>}>
          <SpaceBetween size="m">
            <Box variant="small" color="text-body-secondary"><span style={{ color: '#D13212' }}>*</span> Required field</Box>
            {schema.map(renderField)}
          </SpaceBetween>
        </Container>
      )}

      {schema.length === 0 && configType === 'custom' && (
        <Container header={<Header variant="h3">Payload</Header>}>
          <FormField
            label="Custom Payload (JSON)"
            description="Enter the payload as a JSON object"
            errorText={errors['payload']}
          >
            <Textarea
              value={
                typeof values['_rawPayload'] === 'string'
                  ? (values['_rawPayload'] as string)
                  : JSON.stringify(values, null, 2)
              }
              onChange={({ detail }) => onChange({ _rawPayload: detail.value })}
              rows={10}
              placeholder='{"key": "value"}'
            />
          </FormField>
        </Container>
      )}

    </SpaceBetween>
  );
}
